import { SimNaoEnum } from 'components/form'
import { RacaCorDbEnum } from 'graphql/types.generated'
import {
  beforeEqualToday,
  cep,
  cns,
  cpf,
  createValidator,
  ErrorObject,
  maxLength,
  nome,
  numeroEndereco,
  required,
  telefone,
  validate,
} from 'util/validation'

import {
  DadosClinicosFormModel,
  DadosIdentificacaoFormModel,
  DadosIndividuoFormModel,
  EnderecoResidenciaFormModel,
  NOME_MAX_LENGTH,
  NotificacaoAgravoFormModel,
  NotificacaoFormModel,
} from './model-notificacaoAgravo'

const dadosIdentificacaoValidator = createValidator<DadosIdentificacaoFormModel>(
  {
    dataNotificacao: [required, beforeEqualToday],
    nome: [required, nome(), maxLength(NOME_MAX_LENGTH)],
    nomeSocial: [maxLength(NOME_MAX_LENGTH)],
    // CNS é sempre opcional, com ou sem CPF; quando preenchido precisa ser válido.
    cns: [cns],
    dataNascimento: [required, beforeEqualToday],
    sexo: [required],
    nacionalidade: [required],
    paisNascimento: [required],
    municipioNascimento: [required],
    racaCor: [required],
    isCidadaoAldeado: [required],
    isMembroComunidadeTradicional: [required],
    gestante: [required],
  },
  (values: DadosIdentificacaoFormModel, errors: ErrorObject<DadosIdentificacaoFormModel>) => {
    if (values?.stNaoPossuiCpf) {
      errors.justificativaNaoPossuiCpf = validate(values.justificativaNaoPossuiCpf, required)
    } else {
      errors.cpf = validate(values?.cpf, [required, cpf])
    }

    if (values?.racaCor?.value === RacaCorDbEnum.INDIGENA) {
      errors.etnia = validate(values.etnia, required)
    }

    if (values?.isCidadaoAldeado === SimNaoEnum.SIM) {
      errors.dsei = validate(values.dsei, required)
      errors.poloBase = validate(values.poloBase, required)
      errors.aldeia = validate(values.aldeia, required)
    }

    if (values?.isMembroComunidadeTradicional === SimNaoEnum.SIM) {
      errors.povoComunidadeTradicional = validate(values.povoComunidadeTradicional, required)
    }

    if (!values?.desconheceNomePai) {
      errors.nomePai = validate(values?.nomePai, [required, nome(), maxLength(NOME_MAX_LENGTH)])
    }

    if (!values?.desconheceNomeMae) {
      errors.nomeMae = validate(values?.nomeMae, [required, nome(), maxLength(NOME_MAX_LENGTH)])
    }

    return errors
  }
)

const enderecoResidenciaValidator = createValidator<EnderecoResidenciaFormModel>(
  {
    // TODO: validar regiaoAdministrativa quando existir a tabela (#35076).
    cep: [required, cep],
    uf: [required],
    logradouro: [required],
    complemento: [required, maxLength(30)],
    bairro: [required],
    municipio: [required],
    zona: [required],
    pais: [required],
  },
  (values: EnderecoResidenciaFormModel, errors: ErrorObject<EnderecoResidenciaFormModel>) => {
    errors.numero = values?.semNumero
      ? undefined
      : validate(values?.numero, [required, numeroEndereco(false), maxLength(10)])

    return errors
  }
)

const dadosIndividuoValidator = createValidator<DadosIndividuoFormModel>(
  {
    escolaridade: [required],
    ocupacao: [required],
    // TODO: validar atividadeEconomicaCnae quando existir a tabela CNAE (#35076).
    isProfissionalSaude: [required],
    possuiDeficiencia: [required],
    isPessoaSituacaoRua: [required],
    isPessoaPrivadaLiberdade: [required],
    telefone: [required, telefone],
  },
  (values: DadosIndividuoFormModel, errors: ErrorObject<DadosIndividuoFormModel>) => {
    if (values?.possuiDeficiencia === SimNaoEnum.SIM) {
      errors.deficiencia = validate(values.deficiencia, required)
    }

    errors.endereco = enderecoResidenciaValidator(values?.endereco ?? {})

    return errors
  }
)

const dadosClinicosValidator = createValidator<DadosClinicosFormModel>({
  agravosConfirmados: [required],
  sinaisSintomas: [required],
  outrosSinaisSintomas: [maxLength(100)],
  dataInicioSintomas: [beforeEqualToday],
  dataDiagnostico: [beforeEqualToday],
  dataAtendimento: [beforeEqualToday],
  isDoencaRelacionadaTrabalho: [required],
  isDoencaRelacionadaDesastre: [required],
})

export const dadosIdentificacaoStepValidator = createValidator<NotificacaoAgravoFormModel>({
  dadosIdentificacao: [dadosIdentificacaoValidator],
})

export const dadosIndividuoStepValidator = createValidator<NotificacaoAgravoFormModel>({
  dadosIndividuo: [dadosIndividuoValidator],
})

export const dadosClinicosStepValidator = createValidator<NotificacaoAgravoFormModel>({
  dadosClinicos: [dadosClinicosValidator],
})

const notificacaoValidator = createValidator<NotificacaoFormModel>({
  unidadeSaude: [required],
  municipio: [required],
  uf: [required],
})

export const notificacaoStepValidator = createValidator<NotificacaoAgravoFormModel>({
  notificacao: [notificacaoValidator],
})
